import { c as _c } from "react/compiler-runtime";
import type { AuthorDomProps } from "@multi-tenant-cms/builder/ui/lib/authorDomProps";
import { containerOverlayRgba, hasContainerBackgroundLayers, normalizeContainerBackgroundOpacity, normalizeContainerBackgroundSize, resolveContainerBackgroundImageUrl } from "@multi-tenant-cms/builder/ui/lib/containerBackground";
import type { CanvasContainerTag } from "@multi-tenant-cms/builder/ui/lib/containerTags";
import type { ContainerBackgroundSettings } from "@multi-tenant-cms/builder/ui/types";
import type { CSSProperties, ReactNode } from "react";
type ContainerBackgroundLayersProps = {
  background?: ContainerBackgroundSettings;
  resolvedImageUrl?: string | null;
  children: ReactNode;
  style?: CSSProperties;
  /** Canvas path only: author id/class/data-pb-* attributes, spread onto the box. */
  domProps?: AuthorDomProps;
  /** When true, render only overlay layers + content wrapper (parent supplies positioning). */
  inlineLayersOnly?: boolean;
  /** Tag to render when this component owns the box (canvas path); ignored with `inlineLayersOnly`. */
  as?: CanvasContainerTag;
};
export function ContainerBackgroundLayers(t0) {
  const $ = _c(39);
  const {
    background,
    resolvedImageUrl,
    children,
    style,
    domProps,
    inlineLayersOnly: t1,
    as: t2
  } = t0;
  const inlineLayersOnly = t1 === undefined ? false : t1;
  const Tag = t2 === undefined ? "div" : t2;
  const needsLayers = hasContainerBackgroundLayers(background);
  if (!needsLayers) {
    if (inlineLayersOnly) {
      let t3;
      if ($[0] !== children) {
        t3 = <>{children}</>;
        $[0] = children;
        $[1] = t3;
      } else {
        t3 = $[1];
      }
      return t3;
    }
    let t3;
    if ($[2] !== Tag || $[3] !== children || $[4] !== domProps || $[5] !== style) {
      t3 = <Tag {...domProps} style={style}>{children}</Tag>;
      $[2] = Tag;
      $[3] = children;
      $[4] = domProps;
      $[5] = style;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t3;
  let t4;
  if ($[7] !== background?.imageOpacity || $[8] !== background?.mediaId || $[9] !== background?.overlayColor || $[10] !== background?.overlayOpacity || $[11] !== background?.position || $[12] !== background?.size || $[13] !== resolvedImageUrl) {
    const imageUrl = resolveContainerBackgroundImageUrl(background?.mediaId, resolvedImageUrl);
    const hasImage = Boolean(imageUrl);
    const imageOpacity = normalizeContainerBackgroundOpacity(background?.imageOpacity, 100);
    const overlayColor = background?.overlayColor?.trim() ?? "";
    const overlayOpacity = normalizeContainerBackgroundOpacity(background?.overlayOpacity, 0);
    const hasOverlay = Boolean(overlayColor) && overlayOpacity > 0;
    const t5 = background?.size;
    let t6;
    if ($[16] !== t5) {
      t6 = normalizeContainerBackgroundSize(t5);
      $[16] = t5;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    const size = t6;
    let t7;
    if ($[18] !== background?.position) {
      t7 = background?.position?.trim() || "center";
      $[18] = background?.position;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    const position = t7;
    t3 = hasImage ? <div aria-hidden={true} style={{
      position: "absolute",
      inset: 0,
      backgroundImage: `url(${JSON.stringify(imageUrl)})`,
      backgroundSize: size,
      backgroundPosition: position,
      backgroundRepeat: "no-repeat",
      opacity: imageOpacity / 100,
      zIndex: 0,
      pointerEvents: "none"
    }} /> : null;
    t4 = hasOverlay ? <div aria-hidden={true} style={{
      position: "absolute",
      inset: 0,
      backgroundColor: containerOverlayRgba(overlayColor, overlayOpacity),
      zIndex: 1,
      pointerEvents: "none"
    }} /> : null;
    $[7] = background?.imageOpacity;
    $[8] = background?.mediaId;
    $[9] = background?.overlayColor;
    $[10] = background?.overlayOpacity;
    $[11] = background?.position;
    $[12] = background?.size;
    $[13] = resolvedImageUrl;
    $[14] = t3;
    $[15] = t4;
  } else {
    t3 = $[14];
    t4 = $[15];
  }
  let t5;
  if ($[20] !== t3 || $[21] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const layerNodes = t5;
  let t6;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      position: "relative",
      zIndex: 2
    };
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== children) {
    t7 = <div style={t6}>{children}</div>;
    $[24] = children;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const contentWrap = t7;
  if (inlineLayersOnly) {
    let t8;
    if ($[26] !== contentWrap || $[27] !== layerNodes) {
      t8 = <>{layerNodes}{contentWrap}</>;
      $[26] = contentWrap;
      $[27] = layerNodes;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    return t8;
  }
  const t8 = style?.position ?? "relative";
  const t9 = style?.overflow ?? "hidden";
  let t10;
  if ($[29] !== style || $[30] !== t8 || $[31] !== t9) {
    t10 = {
      ...style,
      position: t8,
      overflow: t9
    };
    $[29] = style;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const baseStyle = t10;
  let t11;
  if ($[33] !== Tag || $[34] !== baseStyle || $[35] !== contentWrap || $[36] !== domProps || $[37] !== layerNodes) {
    t11 = <Tag {...domProps} style={baseStyle}>{layerNodes}{contentWrap}</Tag>;
    $[33] = Tag;
    $[34] = baseStyle;
    $[35] = contentWrap;
    $[36] = domProps;
    $[37] = layerNodes;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
}